// „Spiel wie …“: eine Partie der Bibliothek aus Sicht eines Spielers nachspielen und dessen Züge raten.
// Die Züge der Gegenseite kommen von selbst; nach jedem Rateversuch geht es mit dem Partiezug weiter.
// Punkte vergibt der Server (Stockfish, Guess.java); gleicher Zug und Matt entscheidet die Oberfläche.
import { Chess } from 'chessops/chess';
import { chessgroundDests } from 'chessops/compat';
import { makeFen, parseFen } from 'chessops/fen';
import { makeSanAndPlay } from 'chessops/san';
import { parseSquare } from 'chessops/util';
import type { Key } from '@lichess-org/chessground/types';
import type { Ply } from './api';
import { coachApi } from './coachapi';

export type Side = 'white' | 'black';

/** same = Partiezug getroffen, mate = eigener Zug setzt matt, missed = Partiezug setzt matt, engine = Stockfish */
export interface GuessEntry {
  ply: number;
  guessSan: string;
  masterSan: string;
  points: number;
  loss: number | null;
  kind: 'same' | 'mate' | 'missed' | 'engine';
}

export interface GameMeta {
  id: number;
  white: string;
  black: string;
}

const position = (fen: string) => Chess.fromSetup(parseFen(fen).unwrap()).unwrap();
const squares = (fen: string) => fen.split(' ').slice(0, 2).join(' ');

export class GuessGame {
  i: number;
  entries: GuessEntry[] = [];
  comparing = false;
  error = '';
  private pending: Promise<void> = Promise.resolve();

  /** `delay`: Pause in ms, bevor ein Zug der Gegenseite kommt (Tests: 0) */
  constructor(readonly meta: GameMeta, readonly plies: Ply[], readonly side: Side, start: number,
              private readonly redraw: () => void, private readonly delay = 700) {
    this.i = Math.max(0, Math.min(start, plies.length - 1));
    this.pending = this.opponent();
  }

  get fen() {
    return this.plies[this.i].fen;
  }

  get turn(): Side {
    return this.fen.split(' ')[1] === 'b' ? 'black' : 'white';
  }

  get over() {
    return this.i >= this.plies.length - 1;
  }

  get last(): [Key, Key] | undefined {
    const l = this.plies[this.i].last;
    return l ? [l.slice(0, 2) as Key, l.slice(2, 4) as Key] : undefined;
  }

  get score() {
    return this.entries.reduce((s, e) => s + e.points, 0);
  }

  get max() {
    return this.entries.length * 10;
  }

  get exact() {
    return this.entries.filter(e => e.kind === 'same').length;
  }

  /** wartet, bis Vergleich und Gegenzug durch sind */
  settle() {
    return this.pending;
  }

  dests() {
    return this.over || this.comparing || this.turn !== this.side ? new Map() : chessgroundDests(position(this.fen));
  }

  /** Rateversuch; Umwandlung immer in eine Dame */
  move(orig: Key, dest: Key): Promise<void> {
    const before = this.fen, master = this.plies[this.i + 1];
    const pos = position(before);
    const promo = pos.board.get(parseSquare(orig)!)?.role === 'pawn' && /[18]/.test(dest[1]);
    const guessSan = makeSanAndPlay(pos, { from: parseSquare(orig)!, to: parseSquare(dest)!, promotion: promo ? 'queen' : undefined });
    const after = makeFen(pos.toSetup());
    const entry = { ply: this.i + 1, guessSan, masterSan: master.san ?? '', loss: null };
    this.pending = this.judge(before, master.fen, after, pos.isCheckmate(), entry);
    return this.pending;
  }

  private async judge(before: string, master: string, after: string, mates: boolean, e: Omit<GuessEntry, 'points' | 'kind'>) {
    if (squares(master) === squares(after)) this.entries.push({ ...e, points: 10, kind: 'same' });
    else if (mates) this.entries.push({ ...e, points: 10, kind: 'mate' });
    else if (position(master).isCheckmate()) this.entries.push({ ...e, points: 0, kind: 'missed' });
    else {
      this.comparing = true;
      this.redraw();
      try {
        const r = await coachApi.guess(before, master, after);
        this.entries.push({ ...e, points: r.points, loss: r.loss, kind: 'engine' });
      } catch (err) {
        this.error = (err as Error).message;
      }
      this.comparing = false;
    }
    this.i++;
    this.redraw();
    await this.opponent();
  }

  /** Züge der Gegenseite aus der Partie, mit kurzer Pause, damit man sie sieht */
  private async opponent() {
    while (!this.over && this.turn !== this.side) {
      if (this.delay) await new Promise(r => setTimeout(r, this.delay));
      this.i++;
      this.redraw();
    }
  }
}
